import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppLayout } from '@/components/layout/AppLayout';
import { usePlatformFavicon } from '@/hooks/use-platform-branding';
import { useVisitTracking } from '@/hooks/use-visit-tracking';
import { ProtectedRoute } from '@/routes/ProtectedRoute';
import { useAuthStore } from '@/stores/auth-store';
import { PLATFORM_ROLES } from '@/config/platform-navigation';
import { ApiRequestError } from '@/lib/api-client';

// Halaman dimuat malas agar bundel awal tetap ringan seiring
// bertambahnya modul.
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForcedPasswordChangePage = lazy(() => import('@/pages/auth/ForcedPasswordChangePage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const BlogListPage = lazy(() => import('@/pages/admin/blog/BlogListPage'));
const BlogFormPage = lazy(() => import('@/pages/admin/blog/BlogFormPage'));
const BlogCategoriesPage = lazy(() => import('@/pages/admin/blog/BlogCategoriesPage'));
const SeoSettingsPage = lazy(() => import('@/pages/admin/seo/SeoSettingsPage'));
const PlatformBrandingPage = lazy(() => import('@/pages/admin/branding/PlatformBrandingPage'));
const PlatformCommunityPage = lazy(() => import('@/pages/admin/komunitas/PlatformCommunityPage'));
const PlatformNotificationPage = lazy(
  () => import('@/pages/admin/notifikasi/PlatformNotificationPage'),
);
const PlatformMessagePage = lazy(() => import('@/pages/admin/notifikasi/PlatformMessagePage'));

const SchoolListPage = lazy(() => import('@/pages/sekolah/SchoolListPage'));
const SchoolFormPage = lazy(() => import('@/pages/sekolah/SchoolFormPage'));
const SchoolDetailPage = lazy(() => import('@/pages/sekolah/SchoolDetailPage'));
const AcademicYearListPage = lazy(() => import('@/pages/tahun-ajaran/AcademicYearListPage'));
const StudyGroupListPage = lazy(() => import('@/pages/kelompok/StudyGroupListPage'));
const StudyGroupFormPage = lazy(() => import('@/pages/kelompok/StudyGroupFormPage'));
const TeacherListPage = lazy(() => import('@/pages/guru/TeacherListPage'));
const TeacherFormPage = lazy(() => import('@/pages/guru/TeacherFormPage'));
const TeacherAccountPage = lazy(() => import('@/pages/guru/TeacherAccountPage'));
const StudentListPage = lazy(() => import('@/pages/peserta-didik/StudentListPage'));
const StudentFormPage = lazy(() => import('@/pages/peserta-didik/StudentFormPage'));
const StudentDetailPage = lazy(() => import('@/pages/peserta-didik/StudentDetailPage'));
const StudentImportPage = lazy(() => import('@/pages/peserta-didik/StudentImportPage'));
const StudentMutationPage = lazy(() => import('@/pages/peserta-didik/StudentMutationPage'));
const MutationPrintPage = lazy(() => import('@/pages/peserta-didik/MutationPrintPage'));
const BukuIndukPage = lazy(() => import('@/pages/peserta-didik/BukuIndukPage'));
const BukuIndukPrintPage = lazy(() => import('@/pages/peserta-didik/BukuIndukPrintPage'));
const ParentListPage = lazy(() => import('@/pages/orang-tua/ParentListPage'));
const ParentFormPage = lazy(() => import('@/pages/orang-tua/ParentFormPage'));
const ParentAccountPage = lazy(() => import('@/pages/orang-tua/ParentAccountPage'));
const PortalSettingsPage = lazy(() => import('@/pages/orang-tua/PortalSettingsPage'));
const UserListPage = lazy(() => import('@/pages/pengguna/UserListPage'));
const UserFormPage = lazy(() => import('@/pages/pengguna/UserFormPage'));

const AttendancePage = lazy(() => import('@/pages/kehadiran/AttendancePage'));
const AttendanceRecapPrintPage = lazy(() => import('@/pages/kehadiran/AttendanceRecapPrintPage'));
const AssessmentPage = lazy(() => import('@/pages/penilaian/AssessmentPage'));
const CocurricularActivityListPage = lazy(() => import('@/pages/kokurikuler/CocurricularActivityListPage'));
const CocurricularActivityFormPage = lazy(() => import('@/pages/kokurikuler/CocurricularActivityFormPage'));
const SktbCandidatePage = lazy(() => import('@/pages/sktb/SktbCandidatePage'));
const SktbSettingsPage = lazy(() => import('@/pages/sktb/SktbSettingsPage'));
const LearningPlanPage = lazy(() => import('@/pages/rapor-penilaian/LearningPlanPage'));
const LearningActivityAssessmentPage = lazy(() => import('@/pages/rapor-penilaian/LearningActivityAssessmentPage'));
const CocurricularAssessmentPage = lazy(() => import('@/pages/rapor-penilaian/CocurricularAssessmentPage'));
const SktbPrintPage = lazy(() => import('@/pages/sktb/SktbPrintPage'));
const SuratKeteranganAktifPage = lazy(() => import('@/pages/surat/SuratKeteranganAktifPage'));
const SuratMenerimaPindahanPage = lazy(() => import('@/pages/surat/SuratMenerimaPindahanPage'));
const SuratPengajuanDapodikPage = lazy(() => import('@/pages/surat/SuratPengajuanDapodikPage'));
const SkListPage = lazy(() => import('@/pages/sk/SkListPage'));
const SkFormPage = lazy(() => import('@/pages/sk/SkFormPage'));
const SkPrintPage = lazy(() => import('@/pages/sk/SkPrintPage'));
const AnnouncementListPage = lazy(() => import('@/pages/pengumuman/AnnouncementListPage'));
const AnnouncementFormPage = lazy(() => import('@/pages/pengumuman/AnnouncementFormPage'));
const CalendarPage = lazy(() => import('@/pages/kalender/CalendarPage'));
const JournalPage = lazy(() => import('@/pages/jurnal/JournalPage'));
const JournalPrintPage = lazy(() => import('@/pages/jurnal/JournalPrintPage'));
const JournalSupervisionPage = lazy(() => import('@/pages/jurnal/JournalSupervisionPage'));

const FeeTypeListPage = lazy(() => import('@/pages/tagihan/FeeTypeListPage'));
const FeeTypeFormPage = lazy(() => import('@/pages/tagihan/FeeTypeFormPage'));
const StudentInvoiceListPage = lazy(() => import('@/pages/tagihan/InvoiceListPage'));
const InvoiceGeneratePage = lazy(() => import('@/pages/tagihan/InvoiceGeneratePage'));
const InvoiceFormPage = lazy(() => import('@/pages/tagihan/InvoiceFormPage'));
const PaymentListPage = lazy(() => import('@/pages/pembayaran/PaymentListPage'));
const PaymentFormPage = lazy(() => import('@/pages/pembayaran/PaymentFormPage'));
const PaymentChannelSettingsPage = lazy(() => import('@/pages/pembayaran/PaymentChannelSettingsPage'));
const PaymentConfirmationPage = lazy(() => import('@/pages/pembayaran/PaymentConfirmationPage'));
const PaymentReceiptPage = lazy(() => import('@/pages/pembayaran/PaymentReceiptPage'));
const ReceiptSettingsPage = lazy(() => import('@/pages/pembayaran/ReceiptSettingsPage'));
const ExpenseCategoryListPage = lazy(() => import('@/pages/pengeluaran/ExpenseCategoryListPage'));
const ExpenseCategoryFormPage = lazy(() => import('@/pages/pengeluaran/ExpenseCategoryFormPage'));
const ExpenseListPage = lazy(() => import('@/pages/pengeluaran/ExpenseListPage'));
const ExpenseFormPage = lazy(() => import('@/pages/pengeluaran/ExpenseFormPage'));
const FinanceReportPage = lazy(() => import('@/pages/laporan-keuangan/FinanceReportPage'));

const ItemListPage = lazy(() => import('@/pages/inventaris/ItemListPage'));
const ItemFormPage = lazy(() => import('@/pages/inventaris/ItemFormPage'));
const ItemDetailPage = lazy(() => import('@/pages/inventaris/ItemDetailPage'));
const ItemCategoryPage = lazy(() => import('@/pages/inventaris/ItemCategoryPage'));
const RoomPage = lazy(() => import('@/pages/inventaris/RoomPage'));
const MutationBookPage = lazy(() => import('@/pages/inventaris/MutationBookPage'));
const ConsumablePage = lazy(() => import('@/pages/inventaris/ConsumablePage'));
const InventoryReportPage = lazy(() => import('@/pages/inventaris/InventoryReportPage'));
const InventoryPrintPage = lazy(() => import('@/pages/inventaris/InventoryPrintPage'));

const BroadcastListPage = lazy(() => import('@/pages/broadcast/BroadcastListPage'));
const BroadcastFormPage = lazy(() => import('@/pages/broadcast/BroadcastFormPage'));
const BroadcastDetailPage = lazy(() => import('@/pages/broadcast/BroadcastDetailPage'));
const BroadcastSettingsPage = lazy(() => import('@/pages/broadcast/BroadcastSettingsPage'));
const RaporPage = lazy(() => import('@/pages/rapor/RaporPage'));
const RaporEditorPage = lazy(() => import('@/pages/rapor/RaporEditorPage'));
const RaporPrintPage = lazy(() => import('@/pages/rapor/RaporPrintPage'));
const RaporSettingsPage = lazy(() => import('@/pages/rapor/RaporSettingsPage'));

const NotificationPage = lazy(() => import('@/pages/notifikasi/NotificationPage'));
const TenantSettingsPage = lazy(() => import('@/pages/pengaturan/TenantSettingsPage'));
const SubscriptionPage = lazy(() => import('@/pages/langganan/SubscriptionPage'));
const PortalLayout = lazy(() => import('@/pages/portal/PortalLayout'));
const PortalHomePage = lazy(() => import('@/pages/portal/PortalHomePage'));
const PortalBillingPage = lazy(() => import('@/pages/portal/PortalBillingPage'));
const PortalReceiptPage = lazy(() => import('@/pages/portal/PortalReceiptPage'));
const PortalDevelopmentPage = lazy(() => import('@/pages/portal/PortalDevelopmentPage'));

const PlatformDashboardPage = lazy(() => import('@/pages/admin/PlatformDashboardPage'));
const TenantListPage = lazy(() => import('@/pages/admin/tenant/TenantListPage'));
const TenantFormPage = lazy(() => import('@/pages/admin/tenant/TenantFormPage'));
const PortfolioListPage = lazy(() => import('@/pages/portofolio/PortfolioListPage'));
const PortfolioFormPage = lazy(() => import('@/pages/portofolio/PortfolioFormPage'));
const PlanListPage = lazy(() => import('@/pages/admin/plan/PlanListPage'));
const PlanFormPage = lazy(() => import('@/pages/admin/plan/PlanFormPage'));
const InvoiceListPage = lazy(() => import('@/pages/admin/invoice/InvoiceListPage'));
const SubscriptionChannelPage = lazy(
  () => import('@/pages/admin/langganan/SubscriptionChannelPage'),
);
const SubscriptionConfirmationPage = lazy(
  () => import('@/pages/admin/langganan/SubscriptionConfirmationPage'),
);
const RevenueReportPage = lazy(() => import('@/pages/admin/langganan/RevenueReportPage'));
const VisitorAnalyticsPage = lazy(() => import('@/pages/admin/pengunjung/VisitorAnalyticsPage'));
const TrialStatsPage = lazy(() => import('@/pages/admin/uji-coba/TrialStatsPage'));
const StoragePage = lazy(() => import('@/pages/admin/penyimpanan/StoragePage'));
const PlatformUserPage = lazy(() => import('@/pages/admin/pengguna/PlatformUserPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Error 4xx tidak akan membaik dengan diulang — hanya menunda
        // pesan error sampai ke pengguna.
        if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
          return false;
        }

        return failureCount < 2;
      },
    },
  },
});

function PageFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center" role="status" aria-live="polite">
      {/* Indikator harus terlihat mata, bukan sekadar sr-only: tanpa itu
          halaman yang masih memuat tak bisa dibedakan dari halaman rusak. */}
      <Loader2
        className="size-6 animate-spin text-[var(--color-muted-foreground)]"
        aria-hidden="true"
      />
      <span className="sr-only">Memuat halaman...</span>
    </div>
  );
}

/**
 * Super Admin tidak punya tenant sendiri — dashboard operasional tenant
 * tidak relevan untuknya, jadi "/" merender Dashboard Platform alih-alih
 * DashboardPage biasa. Peran lain tidak terpengaruh sama sekali.
 */
function HomeRoute() {
  const isPlatformUser = useAuthStore((state) => state.hasRole(...PLATFORM_ROLES));
  const canReadPlatformStats = useAuthStore((state) => state.hasPermission('platformStats.read'));
  const isParentOnly = useAuthStore(
    (state) => state.user?.roles.length === 1 && state.user.roles[0] === 'ORANG_TUA',
  );

  // Wali murid tidak punya izin apa pun di dashboard tenant — DashboardPage
  // hanya akan menembak endpoint yang pasti menolaknya. Dialihkan, bukan
  // dirender kosong. Diperiksa sebagai satu-satunya peran: staf yang kebetulan
  // juga wali murid anaknya sendiri tetap masuk ke dashboard, dan bisa membuka
  // portalnya lewat alamat /portal.
  if (isParentOnly) {
    return <Navigate to="/portal" replace />;
  }

  // Marketing berdiri di konsol tetapi tidak boleh membaca ringkasan
  // platform — isinya memuat pemasukan. Dialihkan ke layar yang memang
  // pekerjaannya, bukan dibiarkan mendarat di halaman yang pasti menolaknya.
  if (isPlatformUser) {
    return canReadPlatformStats ? <PlatformDashboardPage /> : <Navigate to="/admin/pengunjung" replace />;
  }

  return <DashboardPage />;
}

/**
 * Memasang favicon unggahan Super Admin.
 *
 * Komponen tersendiri, bukan efek di dalam App, karena hook-nya membaca
 * React Query — dan providernya baru dipasang di dalam App itu sendiri.
 * Tidak merender apa pun; kerjanya hanya menukar <link rel="icon">.
 */
function PlatformFavicon() {
  usePlatformFavicon();
  return null;
}

/**
 * Pencatat kunjungan. Harus berada DI DALAM BrowserRouter — ia membaca
 * perpindahan halaman lewat useLocation.
 */
function VisitTracker() {
  useVisitTracking();
  return null;
}

export default function App() {
  const initialize = useAuthStore((state) => state.initialize);

  // Memulihkan sesi tersimpan sekali saat aplikasi dijalankan.
  useEffect(() => {
    void initialize();
  }, [initialize]);

  return (
    // ErrorBoundary membungkus semuanya: error di provider, router, atau
    // halaman mana pun harus berakhir pada layar yang bisa ditindaklanjuti,
    // bukan halaman putih kosong.
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <PlatformFavicon />
        <BrowserRouter>
          <VisitTracker />
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/masuk" element={<LoginPage />} />
              {/* Pendaftaran mandiri dari halaman harga sipaud.com. Di luar
                  ProtectedRoute: yang membukanya justru belum punya akun. */}
              <Route path="/daftar" element={<RegisterPage />} />

              <Route element={<ProtectedRoute />}>
                <Route path="ganti-sandi-wajib" element={<ForcedPasswordChangePage />} />

                {/* Portal Wali Murid — di luar AppLayout.
                    Sidebar dashboard seluruhnya berisi modul staf; wali yang
                    dimasukkan ke sana hanya akan melihat kerangka kosong. */}
                {/* Di luar PortalLayout: lembar cetak harus bersih dari
                    kepala dan tab navigasi portal. */}
                <Route path="portal/kwitansi/:studentId/:paymentId" element={<PortalReceiptPage />} />

                <Route path="portal" element={<PortalLayout />}>
                  <Route index element={<PortalHomePage />} />
                  <Route path="pembayaran" element={<PortalBillingPage />} />
                  <Route path="perkembangan" element={<PortalDevelopmentPage />} />
                </Route>

                <Route element={<AppLayout />}>
                  <Route index element={<HomeRoute />} />

                  <Route path="admin" element={<PlatformDashboardPage />} />
                  <Route path="admin/tenants" element={<TenantListPage />} />
                  <Route path="admin/tenants/baru" element={<TenantFormPage />} />
                  <Route path="admin/tenants/:id" element={<TenantFormPage />} />
                  <Route path="admin/plans" element={<PlanListPage />} />
                  <Route path="admin/plans/baru" element={<PlanFormPage />} />
                  <Route path="admin/plans/:id" element={<PlanFormPage />} />
                  <Route path="admin/invoices" element={<InvoiceListPage />} />
                  <Route path="admin/langganan/rekening" element={<SubscriptionChannelPage />} />
                  <Route path="admin/langganan/bukti" element={<SubscriptionConfirmationPage />} />
                  <Route path="admin/langganan/pemasukan" element={<RevenueReportPage />} />
                  <Route path="admin/pengunjung" element={<VisitorAnalyticsPage />} />
                  <Route path="admin/uji-coba" element={<TrialStatsPage />} />
                  <Route path="admin/penyimpanan" element={<StoragePage />} />
                  <Route path="admin/akun" element={<PlatformUserPage />} />

                  <Route path="admin/blog" element={<BlogListPage />} />
                  <Route path="admin/blog/baru" element={<BlogFormPage />} />
                  <Route path="admin/blog/kategori" element={<BlogCategoriesPage />} />
                  <Route path="admin/blog/:id" element={<BlogFormPage />} />
                  <Route path="admin/seo" element={<SeoSettingsPage />} />
                  <Route path="admin/merek" element={<PlatformBrandingPage />} />
                  <Route path="admin/komunitas" element={<PlatformCommunityPage />} />
                  <Route path="admin/notifikasi" element={<PlatformNotificationPage />} />
                  <Route path="admin/notifikasi/kirim" element={<PlatformMessagePage />} />

                  <Route path="sekolah" element={<SchoolListPage />} />
                  <Route path="sekolah/baru" element={<SchoolFormPage />} />
                  <Route path="sekolah/:id" element={<SchoolFormPage />} />
                  <Route path="sekolah/:id/detail" element={<SchoolDetailPage />} />

                  <Route path="tahun-ajaran" element={<AcademicYearListPage />} />

                  <Route path="kelompok" element={<StudyGroupListPage />} />
                  <Route path="kelompok/baru" element={<StudyGroupFormPage />} />
                  <Route path="kelompok/:id" element={<StudyGroupFormPage />} />

                  <Route path="guru" element={<TeacherListPage />} />
                  <Route path="guru/akun" element={<TeacherAccountPage />} />
                  <Route path="guru/baru" element={<TeacherFormPage />} />
                  <Route path="guru/:id" element={<TeacherFormPage />} />

                  <Route path="peserta-didik" element={<StudentListPage />} />
                  <Route path="peserta-didik/buku-induk" element={<BukuIndukPage />} />
                  <Route path="peserta-didik/buku-induk/cetak" element={<BukuIndukPrintPage />} />
                  <Route path="peserta-didik/mutasi" element={<StudentMutationPage />} />
                  <Route path="peserta-didik/mutasi/cetak" element={<MutationPrintPage />} />
                  <Route path="peserta-didik/baru" element={<StudentFormPage />} />
                  <Route path="peserta-didik/unggah" element={<StudentImportPage />} />
                  <Route path="peserta-didik/:id" element={<StudentFormPage />} />
                  <Route path="peserta-didik/:id/detail" element={<StudentDetailPage />} />

                  <Route path="orang-tua" element={<ParentListPage />} />
                  <Route path="orang-tua/akun" element={<ParentAccountPage />} />
                  <Route path="orang-tua/portal" element={<PortalSettingsPage />} />
                  <Route path="orang-tua/baru" element={<ParentFormPage />} />
                  {/* Sesudah "akun"/"portal": ':id' akan menelan keduanya bila
                      didaftarkan lebih dulu. */}
                  <Route path="orang-tua/:id" element={<ParentFormPage />} />

                  <Route path="pengguna" element={<UserListPage />} />
                  <Route path="pengguna/baru" element={<UserFormPage />} />
                  <Route path="pengguna/:id" element={<UserFormPage />} />

                  <Route path="kehadiran" element={<AttendancePage />} />
                  <Route path="kehadiran/rekap/cetak" element={<AttendanceRecapPrintPage />} />
                  <Route path="penilaian" element={<AssessmentPage />} />
                  <Route path="kokurikuler" element={<CocurricularActivityListPage />} />
                  <Route path="portofolio" element={<PortfolioListPage />} />
                  <Route path="portofolio/baru" element={<PortfolioFormPage />} />
                  <Route path="portofolio/:id" element={<PortfolioFormPage />} />
                  <Route path="kokurikuler/baru" element={<CocurricularActivityFormPage />} />
                  <Route path="kokurikuler/:id" element={<CocurricularActivityFormPage />} />
                  <Route path="sktb/calon-kelulusan" element={<SktbCandidatePage />} />
                  <Route path="sktb/setelan" element={<SktbSettingsPage />} />

                  <Route path="rapor-penilaian" element={<LearningActivityAssessmentPage />} />
                  <Route path="rapor-penilaian/rencana" element={<LearningPlanPage />} />
                  <Route path="rapor-penilaian/kokurikuler" element={<CocurricularAssessmentPage />} />
                  <Route path="sktb/cetak" element={<SktbPrintPage />} />
                  <Route path="surat/keterangan-aktif" element={<SuratKeteranganAktifPage />} />
                  <Route path="surat/menerima-pindahan" element={<SuratMenerimaPindahanPage />} />
                  <Route path="surat/pengajuan-dapodik" element={<SuratPengajuanDapodikPage />} />
                  <Route path="sk" element={<SkListPage />} />
                  <Route path="sk/baru" element={<SkFormPage />} />
                  <Route path="sk/:id" element={<SkFormPage />} />
                  <Route path="sk/:id/cetak" element={<SkPrintPage />} />
                  <Route path="notifikasi" element={<NotificationPage />} />
                  <Route path="pengaturan" element={<TenantSettingsPage />} />
                  <Route path="langganan" element={<SubscriptionPage />} />
                  <Route path="pengumuman" element={<AnnouncementListPage />} />
                  <Route path="pengumuman/baru" element={<AnnouncementFormPage />} />
                  <Route path="pengumuman/:id" element={<AnnouncementFormPage />} />
                  <Route path="kalender" element={<CalendarPage />} />

                  {/* "cetak" sebelum rute lain agar tidak tertelan. */}
                  <Route path="jurnal" element={<JournalPage />} />
                  <Route path="jurnal/cetak" element={<JournalPrintPage />} />
                  <Route path="jurnal/supervisi" element={<JournalSupervisionPage />} />

                  <Route path="jenis-biaya" element={<FeeTypeListPage />} />
                  <Route path="jenis-biaya/baru" element={<FeeTypeFormPage />} />
                  <Route path="jenis-biaya/:id" element={<FeeTypeFormPage />} />
                  <Route path="tagihan" element={<StudentInvoiceListPage />} />
                  <Route path="tagihan/generate" element={<InvoiceGeneratePage />} />
                  <Route path="tagihan/:id" element={<InvoiceFormPage />} />
                  <Route path="pembayaran" element={<PaymentListPage />} />
                  <Route path="pembayaran/baru" element={<PaymentFormPage />} />
                  <Route path="pembayaran/metode" element={<PaymentChannelSettingsPage />} />
                  <Route path="pembayaran/konfirmasi" element={<PaymentConfirmationPage />} />
                  <Route path="pembayaran/kwitansi-setelan" element={<ReceiptSettingsPage />} />
                  <Route path="pembayaran/kwitansi/:paymentId" element={<PaymentReceiptPage />} />
                  <Route path="pengeluaran/kategori" element={<ExpenseCategoryListPage />} />
                  <Route path="pengeluaran/kategori/baru" element={<ExpenseCategoryFormPage />} />
                  <Route path="pengeluaran/kategori/:id" element={<ExpenseCategoryFormPage />} />
                  <Route path="pengeluaran" element={<ExpenseListPage />} />
                  <Route path="pengeluaran/baru" element={<ExpenseFormPage />} />
                  <Route path="pengeluaran/:id" element={<ExpenseFormPage />} />
                  <Route path="laporan-keuangan" element={<FinanceReportPage />} />

                  {/* Ruas tetap didaftarkan sebelum ':id', jika tidak ':id'
                      akan menelan "kategori", "ruangan", dan seterusnya. */}
                  <Route path="inventaris" element={<ItemListPage />} />
                  <Route path="inventaris/kategori" element={<ItemCategoryPage />} />
                  <Route path="inventaris/ruangan" element={<RoomPage />} />
                  <Route path="inventaris/mutasi" element={<MutationBookPage />} />
                  <Route path="inventaris/persediaan" element={<ConsumablePage />} />
                  <Route path="inventaris/laporan" element={<InventoryReportPage />} />
                  <Route path="inventaris/cetak" element={<InventoryPrintPage />} />
                  <Route path="inventaris/baru" element={<ItemFormPage />} />
                  <Route path="inventaris/:id" element={<ItemFormPage />} />
                  <Route path="inventaris/:id/detail" element={<ItemDetailPage />} />

                  <Route path="broadcast" element={<BroadcastListPage />} />
                  <Route path="broadcast/baru" element={<BroadcastFormPage />} />
                  {/* Sebelum ':id', kalau tidak "setelan" akan ditelan olehnya. */}
                  <Route path="broadcast/setelan" element={<BroadcastSettingsPage />} />
                  <Route path="broadcast/:id" element={<BroadcastDetailPage />} />
                  <Route path="rapor" element={<RaporPage />} />
                  <Route path="rapor/cetak" element={<RaporPrintPage />} />
                  <Route path="rapor/setelan" element={<RaporSettingsPage />} />
                  <Route path="rapor/:studentId" element={<RaporEditorPage />} />

                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>

        <Toaster
          position="top-right"
          richColors
          closeButton
          // Durasi sedikit lebih panjang dari default: pesan berbahasa
          // Indonesia cenderung lebih panjang untuk dibaca.
          duration={5000}
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
